import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const COBITAssessmentForm = () => {
  const [questions, setQuestions] = useState([]); 
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null); 

  const navigate = useNavigate();
  const token = localStorage.getItem("access_token");

  // Load questions and saved responses
  useEffect(() => {
    if (!token) return navigate("/login");

    const fetchData = async () => {
      try {
        const [domainsRes, questionsRes, savedRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}maturity/cobit/domains/`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API_URL}maturity/cobit/questions/`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API_URL}maturity/assessments/load-progress/`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        const domains = domainsRes.data;
        const questionsData = questionsRes.data;
        const savedResponsesData = savedRes.data.cobit_responses || [];

        const savedResponsesMap = {};
        savedResponsesData.forEach(r => {
          const filesArray = r.files ? r.files : (r.file_id ? [{
            id: r.file_id,
            file_url: r.file_url,
            name: r.file_name,
            size: r.file_size
          }] : []);
          
          savedResponsesMap[Number(r.question.id)] = {
            ...r,
            files: filesArray
          };
        });
      
        const groupedByDomain = domains.map(domain => {
          const domainQuestions = questionsData.filter(q => q.domain_code === domain.code);
          return { ...domain, questions: domainQuestions };
        });

        const initialResponses = [];
        groupedByDomain.forEach(domain => {
          domain.questions.forEach(q => {
            const savedResp = savedResponsesMap[Number(q.id)];
            const backendFiles = (savedResp?.files || []).map(f => ({
              id: f.id,
              file_url: f.file_url,
              name: f.name,
              size: f.size
            }));

            initialResponses.push({
              id: savedResp?.id || null,
              question: q.id,
              answer: savedResp?.performed
                ? savedResp.performed.trim().charAt(0).toUpperCase() +
                  savedResp.performed.trim().slice(1).toLowerCase()
                : "",
              evidence: savedResp?.evidence || "",
              backendFiles: backendFiles, 
              newFiles: [],               
            });
          });
        });

        setQuestions(groupedByDomain);
        setResponses(initialResponses);
        if (groupedByDomain.length > 0) setSelectedDomain(0);
        setLoading(false);

      } catch (err) {
        console.error("Failed to load COBIT data", err);
        setError("Failed to load COBIT assessment data.");
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, token]);

  // Save a single response
  const saveSingleResponse = async (respObj, newFiles = []) => {
    try {
      const payload = {
        cobit_question: respObj.question,
        performed: respObj.answer?.toLowerCase() || "",
        evidence: respObj.evidence || "",
      };

      await axios.post(
        `${process.env.REACT_APP_API_URL}maturity/assessments/save-progress/`,
        { responses: [payload] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLastSaved(new Date().toLocaleString());

      if (newFiles && newFiles.length > 0) {
        const progressRes = await axios.get(
          `${process.env.REACT_APP_API_URL}maturity/assessments/load-progress/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const backendResponse = progressRes.data.cobit_responses?.find(
          r => Number(r.question.id) === Number(respObj.question)
        );

        if (!backendResponse?.id) {
          console.warn("No backend response id found for upload.");
          return;
        }
        const responseId = backendResponse.id;

        const uploadedFiles = await Promise.all(
          Array.from(newFiles).map(async (file) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("response_id", responseId);
            formData.append("response_type", "cobit");

            try {
              const res = await axios.post(
                `${process.env.REACT_APP_API_URL}maturity/upload-evidence-file/`,
                formData,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
              );
              return res.data;
            } catch (err) {
              console.error("File upload failed:", err);
              return null;
            }
          })
        );

        setResponses(prev =>
          prev.map(r =>
            Number(r.question) === Number(respObj.question)
              ? {
                  ...r,
                  files: [
                    ...(r.files || []).filter(f => !(f instanceof File)),
                    ...uploadedFiles.filter(Boolean),
                  ],
                  pendingFiles: [],
                }
              : r
          )
        );
      }
    } catch (err) {
      console.error("Autosave failed:", err);
    }
  };

  const handleResponse = (questionId, answer) => {
    const updated = responses.map((r) =>
      Number(r.question) === Number(questionId) ? { ...r, answer } : r
    );
    setResponses(updated);
    const currentResponse = updated.find((r) => Number(r.question) === Number(questionId));
    if (currentResponse) saveSingleResponse(currentResponse, []);
  };

  const handleEvidence = (questionId, evidence) => {
    const updated = responses.map((r) =>
      Number(r.question) === Number(questionId) ? { ...r, evidence } : r
    );
    setResponses(updated);
    const currentResponse = updated.find((r) => Number(r.question) === Number(questionId));
    if (currentResponse) saveSingleResponse(currentResponse, []);
  };

  const handleFilesSelected = (questionId, fileList) => {
    const newFiles = Array.from(fileList || []);
    setResponses((prev) =>
      prev.map((r) =>
        Number(r.question) === Number(questionId)
          ? {
              ...r,
              files: [...(r.files || []), ...newFiles],
              pendingFiles: [...(r.pendingFiles || []), ...newFiles],
            }
          : r
      )
    );

    const localResp = (responses.find((r) => Number(r.question) === Number(questionId)) || {
      question: questionId,
      answer: "",
      evidence: "",
      files: [],
    });
    const mergedResp = {
      ...localResp,
      files: [...(localResp.files || []), ...newFiles],
    };

    saveSingleResponse(mergedResp, newFiles);
  };

  const handleDeleteFile = (questionId, fileIndex) => {
    setResponses((prev) =>
      prev.map((res) =>
        Number(res.question) === Number(questionId)
          ? { ...res, files: res.files.filter((_, i) => i !== fileIndex) }
          : res
      )
    );
  };

  const handleSaveProgress = async () => {
    const payload = responses
      .filter((res) => res.answer || res.evidence)
      .map((res) => ({
        cobit_question: res.question,
        performed: res.answer?.toLowerCase() || "",
        evidence: res.evidence || "",
      }));

    if (payload.length === 0) {
      alert("No responses to save");
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}maturity/assessments/save-progress/`, { responses: payload }, { headers: { Authorization: `Bearer ${token}` } });
      setLastSaved(new Date().toLocaleString());
      alert("Progress saved!");
    } catch (err) {
      console.error("Error saving progress:", err);
      alert("Error saving progress.");
    }
  };

    const getSelectedDomainQuestionIds = () => {
    return questions[selectedDomain]?.questions.map(q => Number(q.id)) || [];
  };

  const handleSubmit = async () => {
    if (!token) {
      navigate("/login");
      return;
    }

    const domainQuestionIds = getSelectedDomainQuestionIds();

    const domainResponses = responses.filter(res =>
      domainQuestionIds.includes(Number(res.question))
    );

    // Validation ONLY for this domain
    for (let res of domainResponses) {
      if (!res.answer) {
        alert("Please answer all questions in this domain.");
        return;
      }

      if (res.answer === "Yes") {
        if (!res.evidence?.trim()) {
          alert(`Evidence required for question ID ${res.question}`);
          return;
        }

        const hasFile =
          Array.isArray(res.files) &&
          res.files.some(f => f instanceof File || f.file_url || f.id);

        if (!hasFile) {
          alert(`Please upload evidence for question ID ${res.question}`);
          return;
        }
      }
    }

    const payload = domainResponses.map(res => ({
      cobit_question: res.question,
      performed: res.answer.toLowerCase(),
      evidence: res.evidence || "",
    }));

    if (payload.length === 0) {
      alert("No responses to submit for this domain.");
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}maturity/assessments/submit/`,
        {
          responses: payload,
          domain_code: questions[selectedDomain].code, // ✅ optional but recommended
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Domain ${questions[selectedDomain].code} submitted successfully!`);
    } catch (err) {
      console.error("Submission error:", err);
      alert("Submission failed.");
    }
  };


  const getProgressForDomain = (domainIndex) => {
    const domainData = questions[domainIndex];
    const domainQuestions = domainData?.questions || [];
    const total = domainQuestions.length;
    const answered = domainQuestions.filter((q) => {
      const r = responses.find((res) => Number(res.question) === Number(q.id));
      if (!r?.answer) return false;
      if (r.answer === "Yes") return r.evidence?.trim() !== "";
      return true;
    }).length;
    const percentage = total ? Math.round((answered / total) * 100) : 0;
    return { total, answered, percentage };
  };

  if (loading) return <div className="p-4 text-center">Loading questions...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6 text-center text-[#FF4D00]">COBIT Assessment Form</h1>

      {questions.length > 0 && (
        <>
          <div className="sticky top-0 z-50 bg-white shadow-md p-2">
            <div className="flex flex-wrap gap-2 justify-center">
              {questions.map((domain, index) => {
                const { answered, total } = getProgressForDomain(index);
                const domainLabel = `${domain.code} | ${domain.title}`;
                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDomain(index)}
                    className={`px-4 py-2 rounded-lg border text-sm ${
                      selectedDomain === index ? "bg-orange-100 border-[#FF4D00]" : "bg-white border-gray-300"
                    }`}
                  >
                    {domainLabel} ({answered}/{total})
                  </button>
                );
              })}
            </div>
          </div>

          {selectedDomain !== null && questions[selectedDomain] && (
            <>
              <div className="sticky top-16 z-40 bg-white px-4 py-3 shadow-md border-b">
                {(() => {
                  const { answered, total, percentage } = getProgressForDomain(selectedDomain);
                  const color = percentage === 100 ? "bg-green-500" : percentage >= 50 ? "bg-yellow-400" : "bg-red-500";
                  return (
                    <>
                      <p className="text-sm text-gray-700 mb-1">
                        Progress for <strong>{questions[selectedDomain].code} - {questions[selectedDomain].title}</strong>: {answered} of {total} answered ({percentage}%)
                      </p>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div className={`${color} h-4 transition-all duration-500`} style={{ width: `${percentage}%` }}></div>
                      </div>
                    </>
                  );
                })()}
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-8 mt-6">
                {Object.entries(
                  questions[selectedDomain].questions.reduce((acc, q) => {
                    const practiceId = q.practice?.id || "no-practice";
                    if (!acc[practiceId]) acc[practiceId] = { practice: q.practice, questions: [] };
                    acc[practiceId].questions.push(q);
                    return acc;
                  }, {})
                ).map(([practiceId, group]) => (
                  <div key={practiceId} className="border-b pb-6">
                    <p className="text-xl font-bold text-[#FF4D00]">
                      Governance Practice: {group.practice?.title || "N/A"}
                    </p>
                    {group.practice?.description && <p className="italic text-sm text-gray-600 mb-4">{group.practice.description}</p>}

                    {group.questions.map((q) => {
                      const current = responses.find((r) => Number(r.question) === Number(q.id)) || { files: [] };
                      return (
                        <div key={q.id} className="mb-8">
                          <label className="block font-medium">Question: {q.request}</label>

                          <select
                            value={current.answer || ""}
                            onChange={(e) => handleResponse(q.id, e.target.value)}
                            className={`mt-2 w-full border rounded-md p-2 focus:outline-none appearance-none ${
                              current.answer === "Yes" ? "bg-green-100 border-green-500 text-green-800" :
                              current.answer === "No" ? "bg-red-100 border-red-500 text-red-800" : "bg-white border-gray-300 text-gray-800"
                            }`}
                          >
                            <option value="">Select</option>
                            <option value="Yes">Yes</option>
                            <option value="No">No</option>
                          </select>

                          {current.answer === "Yes" && (
                            <>
                              <input
                                type="text"
                                placeholder="Describe Evidence"
                                value={current.evidence || ""}
                                onChange={(e) => handleEvidence(q.id, e.target.value)}
                                className="mt-2 block w-full border border-gray-300 rounded-md p-2"
                              />

                              <input
                                type="file"
                                multiple
                                onChange={(e) => handleFilesSelected(q.id, e.target.files)}
                                className="mt-2 block w-full text-sm text-gray-500
                                          file:mr-4 file:py-2 file:px-4 file:border
                                          file:rounded-md file:text-sm file:font-semibold
                                          file:bg-[#FF4D00] file:text-white hover:file:bg-orange-600"
                              />

                              {current.files?.length > 0 && (
                                <div className="mt-3 space-y-2">
                                  {current.files.map((file, index) => {
                                    const fileName = file.name || (file.file_url ? file.file_url.split("/").pop() : "Unnamed File");
                                    const fileUrl = file instanceof File ? URL.createObjectURL(file) : (file.file_url || file.url || "#");
                                    const fileSize = file.size || file.file_size || 0;

                                    return (
                                      <div key={index} className="flex items-center justify-between bg-gray-100 p-2 rounded-md shadow-sm">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xl">{String(fileName).toLowerCase().endsWith(".pdf") ? "🗎" : "📄"}</span>
                                          <div>
                                            <p className="text-sm font-medium">{fileName}</p>
                                            {fileSize > 0 && <p className="text-xs text-gray-500">{`${(fileSize / 1024).toFixed(1)} KB`}</p>}
                                          </div>
                                        </div>
                                        <div className="flex gap-2">
                                          <a href={fileUrl} download={fileName} className="text-blue-600 text-sm hover:underline" target="_blank" rel="noopener noreferrer">Download</a>
                                          <button type="button" onClick={() => handleDeleteFile(q.id, index)} className="text-red-500 text-sm hover:underline">Delete</button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}

                <div className="flex gap-4 justify-center mt-6">
                  <button type="submit" className="bg-[#FF4D00] text-white px-4 py-2 rounded hover:bg-orange-700">Submit Assessment</button>
                  <button type="button" onClick={handleSaveProgress} className="bg-[#FF4D00] text-white px-4 py-2 rounded hover:bg-orange-700">Save Progress</button>
                  <button type="button" onClick={() => navigate("/cobit/dashboard")} className="bg-[#FF4D00] text-white px-4 py-2 rounded hover:bg-orange-700">Go to Dashboard</button>
                </div>

                {lastSaved && <p className="text-sm text-gray-600 mt-2">Last saved: {lastSaved}</p>}
              </form>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default COBITAssessmentForm;
